import { CONFIG } from '../helper/config';
import { getRequestOptions, handleResponse, handleError } from './service-utilities';

/**
 * Service providing API calls related to card library operations such as
 * fetching cards, filters, dropdown data, exporting CSVs, and user location.
 *
 * Each method wraps a `fetch` request with the appropriate endpoint,
 * request method, and authentication token handling.
 */
export const cardLibraryService = {
    /** Fetches team card data for given info and auth token. */
    getTeamCard,

    /** Fetches the dependent filter data for the given info, port number and auth token. */
    getDependentFilterData,

    /** Updates the filter data for the given info, port number and auth token. */
    updateFilterData,

    /** Fetches the sorted dependent filter data for the given info, port number and auth token. */
    getSortedDependentFilterData,

    /** Fetches multiple dropdown options for the given info and auth token. */
    getMultipleDropdownOptions,

    /** Downloads CSV data for the given info and authentication token. */
    downloadCsv,

    /** Fetches user location data for given info and auth token. */
    fetchUserLocation
}

/**
 * Fetches team card data for given info and auth token.
 * @param {object} info The info object containing the necessary data.
 * @param {string} auth_token The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getTeamCard(info: any, auth_token:string) {
    let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND  + CONFIG.API.REQUESTURL.GETTEAMCARD;    
    let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST , auth_token, JSON.stringify(info))
    return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches the dependent filter data for the given info, port number and auth token.
 * @param {object} info The info object containing the necessary data.
 * @param {string} port_no The port number to use for the request.
 * @param {string} auth_token The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getDependentFilterData(info: any, port_no: string, auth_token:string) {
    let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.DEPENDENDFILTER;    
    let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST , auth_token, JSON.stringify(info))
    return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Updates the filter data for the given info, port number and auth token.
 * @param {object} info The info object containing the necessary data.
 * @param {string} port_no The port number to use for the request.
 * @param {string} auth_token The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function updateFilterData(info: any, port_no: string, auth_token:string) {
    let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.PROCESSDEPENDENDFILTER;    
    let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST , auth_token, JSON.stringify(info))
    return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches the sorted dependent filter data for the given info, port number and auth token.
 * @param {object} info The info object containing the necessary data.
 * @param {string} port_no The port number to use for the request.
 * @param {string} auth_token The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getSortedDependentFilterData(info: any, port_no: string, auth_token:string) {
    let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + port_no + CONFIG.API.REQUESTURL.SORTEDFILTERS;    
    let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST , auth_token, JSON.stringify(info))
    return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches multiple dropdown options for the given info and auth token.
 * @param {object} info The info object containing the necessary data.
 * @param {string} auth_token The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */

function getMultipleDropdownOptions(info: any, auth_token:string) {
    let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.COLUMNFILTERDATA;    
    let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST , auth_token , JSON.stringify(info))
    return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Downloads CSV data for the given info and authentication token.
 * @param {object} info - The info object containing the necessary data for the request.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function downloadCsv(info: any , auth_token: string){
    let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.DOWNLOADCSV;    
    let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST , auth_token , JSON.stringify(info))
    return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches user location data for given info and auth token.
 * @param {object} info The info object containing the necessary data.
 * @param {string} auth_token The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function fetchUserLocation(info: any , auth_token: string){
    let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.GETLOCATION;    
    let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST , auth_token , JSON.stringify(info))
    return fetch(url, request_options).then(handleResponse).catch(handleError);
}